import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { apiConfig, loginRequest } from './authConfig';
import { client } from '../api/generated/client.gen';
import '../api/client'; // registers auth interceptor for direct client.instance calls

const TOKEN_REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes

const AccessTokenContext = createContext<string | undefined>(undefined);

export const AccessTokenProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { instance, accounts } = useMsal();
  const [accessToken, setAccessToken] = useState<string | undefined>(undefined);
  const intervalRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    if (!accounts[0]) return;

    const request = {
      ...loginRequest,
      account: accounts[0],
    };

    const acquireToken = async () => {
      try {
        const response = await instance.acquireTokenSilent(request);
        setAccessToken(response.accessToken);
        client.setConfig({
          baseURL: apiConfig.baseUrl,
          auth: response.accessToken,
        });
      } catch {
        // Silent refresh failed — redirect to login.
        // MSAL returns the user to the same page after sign-in.
        await instance.acquireTokenRedirect(request);
      }
    };

    // Refresh token immediately when tab/window becomes visible (e.g. after
    // PC sleep). This runs before React Query's refetchOnWindowFocus fires,
    // ensuring queries use a fresh token instead of the expired one.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        acquireToken();
      }
    };

    acquireToken();
    intervalRef.current = setInterval(acquireToken, TOKEN_REFRESH_INTERVAL);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [instance, accounts]);

  return (
    <AccessTokenContext.Provider value={accessToken}>
      {children}
    </AccessTokenContext.Provider>
  );
};

export const useAccessToken = (): string | undefined => {
  return useContext(AccessTokenContext);
};
